// แปลงไฟล์ "รายชื่อนักศึกษาลงทะเบียน" (repclasslist.xlsx ของ CMRU) เป็นข้อมูลที่ส่งเข้า API นำเข้าได้
// รับ rows แบบ array ซ้อน array (XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: "" }))
//
// โครงไฟล์: ส่วนหัว ~5 บรรทัดเป็นข้อความอิสระ -> แถวหัวตาราง (เลขที่ | รหัสประจำตัว | ชื่อ | หมู่เรียน | สัปดาห์ 1-16) -> รายชื่อ
// คอลัมน์ "สัปดาห์" และแถวสรุปท้ายตาราง (ไม่มีรหัสประจำตัว) ไม่ถูกเก็บ

// เรียงจากยาวไปสั้น ไม่งั้น "นาง" จะตัด "นางสาว" ผิด
const NAME_PREFIXES = [
  "ว่าที่ร้อยตรีหญิง", "ว่าที่ ร.ต.หญิง", "ว่าที่ร้อยตรี", "ว่าที่ ร.ต.",
  "นางสาว", "สามเณร", "นาง", "นาย", "พระ", "Miss", "Mrs.", "Mr.", "Ms.",
].sort((a, b) => b.length - a.length);

const text = (value) => String(value ?? "").replace(/\s+/g, " ").trim();

export const splitStudentName = (fullName) => {
  let rest = text(fullName);
  const prefix = NAME_PREFIXES.find((item) => rest.startsWith(item)) || "";
  if (prefix) rest = rest.slice(prefix.length).trim();

  const [firstName = "", ...lastParts] = rest.split(" ");
  return { prefix_name: prefix, first_name: firstName, last_name: lastParts.join(" ") };
};

// "F 08/10/26 13:00-15:00, M 06/08/26 13:00-15:00" -> F = ปลายภาค, M = กลางภาค
const parseExams = (value) => {
  const result = { midterm_exam: "", final_exam: "" };
  text(value).split(",").map(text).filter(Boolean).forEach((part) => {
    const match = part.match(/^([FM])\s+(.+)$/i);
    if (!match) return;
    if (match[1].toUpperCase() === "M") result.midterm_exam = match[2];
    else result.final_exam = match[2];
  });
  return result;
};

const COLUMN_LABELS = { seq_no: "เลขที่", student_code: "รหัสประจำตัว", name: "ชื่อ", student_group: "หมู่เรียน" };

export const parseClassListRows = (rows = []) => {
  const warnings = [];

  const headerIndex = rows.findIndex((row) => (row || []).some((cell) => text(cell) === COLUMN_LABELS.student_code));
  if (headerIndex < 0) {
    return { section: null, students: [], warnings, error: "ไม่พบหัวตาราง \"รหัสประจำตัว\" ไฟล์นี้อาจไม่ใช่ใบรายชื่อนักศึกษาลงทะเบียน" };
  }

  // ---------- ส่วนหัวเอกสาร ----------
  const headerTexts = rows.slice(0, headerIndex).flatMap((row) => (row || []).map(text).filter(Boolean));
  const section = {
    academic_year: "", semester: "", campus: "", program_type: "",
    course_code: "", course_name: "", credits: "", section_no: "",
    instructor_name: "", class_schedule: "", midterm_exam: "", final_exam: "",
  };

  headerTexts.forEach((line, index) => {
    let match;
    if ((match = line.match(/ภาคการศึกษา\s*(\S+?)\s*\/\s*(\d{4})/))) {
      section.semester = match[1];
      section.academic_year = match[2];
      const campus = line.match(/วิทยาเขต\s*(.+?)\s*ภาคการศึกษา/);
      if (campus) section.campus = campus[1];
      // บรรทัดถัดจากภาคการศึกษาคือระดับ/ประเภทนักศึกษา เช่น "ปริญญาตรี (4 ปี) ภาคปกติ"
      const next = headerTexts[index + 1] || "";
      if (next && !/^(รหัสวิชา|ผู้สอน|วันเวลา)/.test(next)) section.program_type = next;
    } else if ((match = line.match(/^รหัสวิชา\s*(\S+)\s+(.+)$/))) {
      section.course_code = match[1];
      let rest = match[2];
      const sec = rest.match(/\s+Sec\.?\s*(\S+)\s*$/i);
      if (sec) { section.section_no = sec[1]; rest = rest.slice(0, sec.index); }
      const credits = rest.match(/\s+หน่วยกิต\s*(\S+)\s*$/);
      if (credits) { section.credits = credits[1]; rest = rest.slice(0, credits.index); }
      section.course_name = text(rest);
    } else if ((match = line.match(/^ผู้สอน\s*(.+)$/))) {
      section.instructor_name = match[1];
    } else if ((match = line.match(/^วันเวลาเรียน\s*(.+)$/))) {
      section.class_schedule = match[1];
    } else if ((match = line.match(/^วันเวลาสอบ\s*(.+)$/))) {
      Object.assign(section, parseExams(match[1]));
    }
  });

  [["academic_year", "ปีการศึกษา"], ["semester", "ภาคการศึกษา"], ["course_code", "รหัสวิชา"], ["course_name", "ชื่อวิชา"], ["section_no", "Sec"]]
    .filter(([field]) => !section[field])
    .forEach(([, label]) => warnings.push(`อ่าน${label}จากหัวเอกสารไม่ได้ กรุณากรอกเองก่อนบันทึก`));

  // ---------- รายชื่อ ----------
  const headerRow = rows[headerIndex].map(text);
  const columns = Object.fromEntries(Object.entries(COLUMN_LABELS).map(([field, label]) => [field, headerRow.indexOf(label)]));

  const students = [];
  let skipped = 0;
  rows.slice(headerIndex + 1).forEach((row) => {
    const cells = (row || []).map(text);
    const studentCode = columns.student_code >= 0 ? cells[columns.student_code] : "";
    const fullName = columns.name >= 0 ? cells[columns.name] : "";

    if (!/^\d{5,}$/.test(studentCode || "")) {
      // แถวว่าง แถวเลขสัปดาห์ และแถวสรุปท้ายตาราง ข้ามเงียบ ๆ นับเฉพาะแถวที่ดูเหมือนรายชื่อแต่รหัสผิดรูปแบบ
      if (fullName) skipped += 1;
      return;
    }

    const seqNo = Number(columns.seq_no >= 0 ? cells[columns.seq_no] : "");
    students.push({
      seq_no: Number.isInteger(seqNo) && seqNo > 0 ? seqNo : students.length + 1,
      student_code: studentCode,
      ...splitStudentName(fullName),
      student_group: columns.student_group >= 0 ? cells[columns.student_group] : "",
    });
  });

  if (skipped) warnings.push(`ข้าม ${skipped} แถวที่มีชื่อแต่รหัสประจำตัวไม่ใช่ตัวเลข`);
  students.filter((item) => !item.first_name).forEach((item) => warnings.push(`รหัส ${item.student_code} ไม่มีชื่อ`));

  const codes = students.map((item) => item.student_code);
  const duplicates = [...new Set(codes.filter((code, index) => codes.indexOf(code) !== index))];
  if (duplicates.length) warnings.push(`รหัสซ้ำในไฟล์: ${duplicates.join(", ")} (ระบบจะเก็บแถวแรก)`);

  return { section, students, warnings, error: students.length ? "" : "ไม่พบรายชื่อนักศึกษาในไฟล์" };
};
